import { useState, useEffect, useMemo } from 'react';
import { GitBranch, ChevronRight, ChevronDown, Circle, CheckCircle2, Clock, AlertCircle, TrendingUp, Users, Calendar, Link2 } from 'lucide-react';
import { projectWbsService, buildWbsTree, type ProjectWBSItem } from '../../services/projectWbs';
import { toast } from 'sonner';

export interface WBSItem {
  id: string;
  level: number;
  name: string;
  jiraId: string;
  jiraType: string;
  status: string;
  progress: number;
  assignee: string;
  dueDate: string;
  effort: string;
  children: WBSItem[];
}

interface WBSSectionProps {
  projectId?: number;
}

function countNodes(items: WBSItem[]): number {
  return items.reduce((sum, item) => 1 + countNodes(item.children), 0);
}

function countByStatus(items: WBSItem[], status: string): number {
  return items.reduce(
    (sum, item) => (item.status === status ? 1 : 0) + countByStatus(item.children, status),
    0
  );
}

function totalEffort(items: WBSItem[]): number {
  return items.reduce((sum, item) => {
    const h = parseInt(item.effort.replace(/h/g, ''), 10) || 0;
    return sum + h + totalEffort(item.children);
  }, 0);
}

export function WBSSection({ projectId }: WBSSectionProps) {
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [rawItems, setRawItems] = useState<ProjectWBSItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!projectId) {
      setRawItems([]);
      return;
    }
    setLoading(true);
    projectWbsService
      .listItems(projectId)
      .then(setRawItems)
      .catch(() => toast.error('Failed to load WBS'))
      .finally(() => setLoading(false));
  }, [projectId]);

  const wbsData = useMemo(() => {
    // Always the project's own data, even when empty (the old sample WBS
    // was removed on 2026-10-03).
    if (projectId) return buildWbsTree(rawItems);
    return [];
  }, [projectId, rawItems]);

  useEffect(() => {
    if (projectId && rawItems.length > 0) {
      const rootIds = buildWbsTree(rawItems).map((t) => t.id);
      setExpandedItems((prev) => (prev.length === 0 ? rootIds : prev));
    }
  }, [projectId, rawItems]);

  const toggleExpand = (id: string) => {
    setExpandedItems(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const totalPackages = countNodes(wbsData);
  const completed = countByStatus(wbsData, 'completed');
  const inProgress = countByStatus(wbsData, 'in-progress');
  const notStarted = countByStatus(wbsData, 'not-started');
  const effortTotal = totalEffort(wbsData);
  const completionRate = totalPackages > 0 ? Math.round((completed / totalPackages) * 100) : 0;

  const insights = [
    { label: 'Total Work Packages', value: String(totalPackages), change: projectId ? 'From project' : 'Sample data', trend: 'up' as const, icon: GitBranch },
    { label: 'Completion Rate', value: `${completionRate}%`, change: `${completed} completed`, trend: 'up' as const, icon: TrendingUp },
    { label: 'On Schedule', value: `${completed + inProgress}/${totalPackages}`, change: notStarted > 0 ? `${notStarted} not started` : 'All active', trend: notStarted > completed ? 'warning' : 'success', icon: Calendar },
    { label: 'Jira Sync Status', value: projectId ? 'Linked' : 'Demo', change: projectId ? 'Use API data' : 'Static sample', trend: 'success' as const, icon: Link2 },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50';
      case 'in-progress': return 'text-blue-600 bg-blue-50';
      case 'at-risk': return 'text-orange-600 bg-orange-50';
      case 'not-started': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-4 h-4" />;
      case 'in-progress': return <Clock className="w-4 h-4" />;
      case 'at-risk': return <AlertCircle className="w-4 h-4" />;
      case 'not-started': return <Circle className="w-4 h-4" />;
      default: return <Circle className="w-4 h-4" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'in-progress': return 'In Progress';
      case 'at-risk': return 'At Risk';
      case 'not-started': return 'Not Started';
      default: return status;
    }
  };

  const renderWBSItem = (item: WBSItem, depth: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);

    return (
      <div key={item.id}>
        <div
          className={`grid items-center gap-4 p-3 hover:bg-gray-50 border-b border-gray-100 transition-colors ${depth === 0 ? 'bg-blue-50/30' : ''}`}
          style={{
            gridTemplateColumns: '40px 120px 1fr 140px 140px 140px 160px 110px 80px',
            paddingLeft: `${depth * 24 + 12}px`
          }}
        >
          <button onClick={() => toggleExpand(item.id)} className="flex-shrink-0" disabled={!hasChildren}>
            {hasChildren ? (isExpanded ? <ChevronDown className="w-4 h-4 text-gray-600" /> : <ChevronRight className="w-4 h-4 text-gray-600" />) : <div className="w-4 h-4" />}
          </button>
          <div className="flex-shrink-0"><span className="text-sm font-mono text-gray-900">{item.id}</span></div>
          <div className="min-w-0"><span className={`text-sm ${depth === 0 ? 'font-medium' : ''} truncate block`}>{item.name}</span></div>
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2 mb-0.5">
              <Link2 className="w-3 h-3 text-blue-600" />
              <span className="text-xs font-mono text-blue-600">{item.jiraId}</span>
            </div>
            <span className="text-xs text-gray-500">{item.jiraType}</span>
          </div>
          <div className="flex-shrink-0">
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs ${getStatusColor(item.status)}`}>
              {getStatusIcon(item.status)}
              <span>{getStatusLabel(item.status)}</span>
            </div>
          </div>
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                <div className={`h-1.5 rounded-full ${item.progress === 100 ? 'bg-green-500' : item.progress >= 50 ? 'bg-blue-500' : item.progress > 0 ? 'bg-yellow-500' : 'bg-gray-300'}`} style={{ width: `${item.progress}%` }}></div>
              </div>
              <span className="text-xs text-gray-600 w-10 text-right">{item.progress}%</span>
            </div>
          </div>
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Users className="w-3 h-3 text-blue-600" />
              </div>
              <span className="text-xs text-gray-700 truncate">{item.assignee}</span>
            </div>
          </div>
          <div className="flex-shrink-0"><span className="text-xs text-gray-700">{item.dueDate}</span></div>
          <div className="flex-shrink-0 text-right"><span className="text-xs font-medium text-gray-700">{item.effort}</span></div>
        </div>
        {hasChildren && isExpanded && <div>{item.children.map((child) => renderWBSItem(child, depth + 1))}</div>}
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      <div className="bg-gradient-to-r from-[#486581] via-[#5c748e] to-[#486581] rounded-xl p-8 text-white border-b-4 border-[#DAA520]">
        <div className="mb-6">
          <h1 className="text-3xl mb-2">Work Breakdown Structure (WBS)</h1>
          <p className="text-[#d4dce5]">Integrated from Jira - Bi-directional sync enabled</p>
        </div>
        <div className="grid grid-cols-4 gap-6">
          {insights.map((insight, idx) => {
            const Icon = insight.icon;
            return (
              <div key={idx} className="bg-white rounded-lg p-5">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-3 ${insight.trend === 'success' ? 'bg-green-50' : insight.trend === 'warning' ? 'bg-orange-50' : 'bg-blue-50'}`}>
                  <Icon className={`w-6 h-6 ${insight.trend === 'success' ? 'text-green-600' : insight.trend === 'warning' ? 'text-orange-600' : 'text-blue-600'}`} />
                </div>
                <div className="text-sm text-gray-600 mb-2">{insight.label}</div>
                <div className="text-3xl font-medium text-neutral-950 mb-1">{insight.value}</div>
                <div className={`text-sm ${insight.trend === 'success' ? 'text-green-600' : insight.trend === 'warning' ? 'text-orange-600' : insight.trend === 'up' ? 'text-green-600' : 'text-gray-600'}`}>{insight.change}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <div className="bg-gray-50 border-b border-gray-200 p-3 grid items-center gap-4 sticky top-0 z-10" style={{ gridTemplateColumns: '40px 120px 1fr 140px 140px 140px 160px 110px 80px' }}>
            <div className="flex-shrink-0" />
            <div className="flex-shrink-0"><span className="text-xs font-medium text-gray-700">WBS ID</span></div>
            <div className="min-w-0"><span className="text-xs font-medium text-gray-700">Work Package Name</span></div>
            <div className="flex-shrink-0"><span className="text-xs font-medium text-gray-700">Jira Mapping</span></div>
            <div className="flex-shrink-0"><span className="text-xs font-medium text-gray-700">Status</span></div>
            <div className="flex-shrink-0"><span className="text-xs font-medium text-gray-700">Progress</span></div>
            <div className="flex-shrink-0"><span className="text-xs font-medium text-gray-700">Assignee</span></div>
            <div className="flex-shrink-0"><span className="text-xs font-medium text-gray-700">Due Date</span></div>
            <div className="flex-shrink-0 text-right"><span className="text-xs font-medium text-gray-700">Effort</span></div>
          </div>
          <div className="max-h-[600px] overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading WBS...</div>
            ) : (
              wbsData.map(item => renderWBSItem(item, 0))
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        <div className="grid grid-cols-5 gap-6">
          <div className="text-center">
            <div className="text-sm text-gray-600 mb-1">Total Work Packages</div>
            <div className="text-2xl font-medium text-neutral-950">{totalPackages}</div>
          </div>
          <div className="text-center border-l border-gray-200">
            <div className="text-sm text-gray-600 mb-1">Completed</div>
            <div className="text-2xl font-medium text-green-600">{completed}</div>
          </div>
          <div className="text-center border-l border-gray-200">
            <div className="text-sm text-gray-600 mb-1">In Progress</div>
            <div className="text-2xl font-medium text-blue-600">{inProgress}</div>
          </div>
          <div className="text-center border-l border-gray-200">
            <div className="text-sm text-gray-600 mb-1">Not Started</div>
            <div className="text-2xl font-medium text-gray-600">{notStarted}</div>
          </div>
          <div className="text-center border-l border-gray-200">
            <div className="text-sm text-gray-600 mb-1">Total Effort</div>
            <div className="text-2xl font-medium text-neutral-950">{effortTotal}h</div>
          </div>
        </div>
      </div>
    </div>
  );
}
